import { Directive, Host, Self, Optional, ViewContainerRef, Renderer2, AfterViewInit, OnChanges, Input, SimpleChanges } from '@angular/core'
import { MatPaginator } from '@angular/material/paginator'
import { TranslateService } from '@ngx-translate/core'
import { ActivatedRoute } from '@angular/router'

@Directive({
  selector: '[linPaginatorCustom]',
  standalone: false,
})
export class PaginatorCustomDirective implements AfterViewInit, OnChanges {
  @Input() pages: number

  pagePaginatorContent: HTMLElement

  get currentPage(): number {
    return this.paginator.pageIndex + 1
  }

  get pagesCount(): number {
    return this.paginator.getNumberOfPages()
  }

  constructor(
    @Host() @Self() @Optional() private paginator: MatPaginator,
    private viewRef: ViewContainerRef,
    private renderer: Renderer2,
    private translate: TranslateService,
    private route: ActivatedRoute,
  ) {
    const tabKey = this.getTabKey()
    const savedPageSize = localStorage.getItem(`linPaginatorPageSize_${tabKey}`)
    paginator.pageSize = savedPageSize ? +savedPageSize : 10
    paginator.pageSizeOptions = [5, 10, 25, 100]
  }

  ngAfterViewInit(): void {
    this.buildPaginator()

    this.paginator.page.subscribe((): void => {
      this.setContent()
      const tabKey = this.getTabKey()
      this.savePageSize(tabKey)
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.pages?.currentValue) {
      this.setContent()
    }
  }

  getTabKey(): string {
    return this.route.snapshot.pathFromRoot.map((v) => v.url.map((segment) => segment.toString()).join('/')).join('/')
  }

  buildPaginator(): void {
    const nativeElement = <HTMLElement>this.viewRef.element.nativeElement
    this.renderer.addClass(nativeElement, 'lin-paginator')

    //select paginator container
    const container = nativeElement.childNodes[0].childNodes[0]

    //remove label and range paginator
    const selectLabel = container.childNodes[0].childNodes[0]
    this.renderer.removeChild(container.childNodes[0], selectLabel)
    const rangePaginator = container.childNodes[2]
    this.renderer.removeChild(container, rangePaginator)

    //insert label after selector
    this.renderer.appendChild(container, selectLabel)
    selectLabel.textContent = <string>this.translate.instant('MAIN.ITEMS_PER_PAGE')

    //create page paginator
    const pagePaginator = <HTMLElement>this.renderer.createElement('div')
    this.renderer.addClass(pagePaginator, 'lin-page-switcher')

    //button create
    const createButton = (iconName: string): HTMLElement => {
      const button = <HTMLElement>this.renderer.createElement('button')
      this.renderer.addClass(button, 'lin-page-switcher-button')

      const icon = <HTMLElement>this.renderer.createElement('span')
      this.renderer.addClass(icon, 'material-icons-outlined')
      const iconText = <HTMLElement>this.renderer.createText(iconName)

      this.renderer.appendChild(icon, iconText)
      this.renderer.appendChild(button, icon)

      return button
    }

    //create buttons
    const prevButton = createButton('arrow_left')
    const nextButton = createButton('arrow_right')
    this.renderer.listen(prevButton, 'click', (): void => this.paginator.previousPage())
    this.renderer.listen(nextButton, 'click', (): void => this.paginator.nextPage())

    //create content
    const pagePaginatorCenter = <HTMLElement>this.renderer.createElement('div')
    this.renderer.addClass(pagePaginatorCenter, 'lin-page-switcher-content')
    this.pagePaginatorContent = <HTMLElement>this.renderer.createElement('span')
    this.pagePaginatorContent.textContent = <string>this.translate.instant('ABBREVIATIONS.OF', { current: 0, total: 0 })

    this.renderer.appendChild(pagePaginatorCenter, this.pagePaginatorContent)
    this.renderer.appendChild(pagePaginator, prevButton)
    this.renderer.appendChild(pagePaginator, pagePaginatorCenter)
    this.renderer.appendChild(pagePaginator, nextButton)
    this.renderer.appendChild(container, pagePaginator)
  }

  setContent(): void {
    this.pagePaginatorContent.textContent = <string>this.translate.instant('ABBREVIATIONS.OF', {
      current: this.currentPage,
      total: this.pagesCount,
    })
  }

  //save the page size to local storage
  savePageSize(tabKey: string): void {
    localStorage.setItem(`linPaginatorPageSize_${tabKey}`, this.paginator.pageSize.toString())
  }
}
